import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { GridLayout } from 'components/grid/GridLayout';
import { FormCheckbox, FormDateInput, FormHelperText, FormSwitch } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { NotificationInline } from 'components/notifications';
import {
    buildDefaultTachoTopicSettings,
    getOfferedTriggerTypes,
    getTachoInfringementsTopicId,
    isTachoInfringementsTopicSelected,
    toCalendarDate
} from 'pages/employee-reviews/review-types/shared/reviewTypeEditorHelpers';
import { ReviewTriggersTable } from 'pages/employee-reviews/shared';
import './ReviewTypeEditor.css';

const defaultObject = {};
const multiplePermissionsSummary = "Multiple selected topics require permissions that not all users will have:";

// The picker's own lower limit is a rolling 30 months ago, which would stop an administrator
// bringing in older infringement history. Held at module level so the reference stays stable.
const includeItemsFromEarliestYear = 2000;
const includeItemsFromMinDate = new Date(includeItemsFromEarliestYear, 0, 1);

export function ReviewTypeTopicsStep(
    {
        reviewType,
        onUpdate,
        errors = defaultObject,
        triggerErrors = defaultObject,
        reviewOptions = defaultObject,
        loading = false,
        saving = false,
        createMode = false
    }
) {

    const tachoTopicSettings = reviewType?.tachoTopicSettings ?? buildDefaultTachoTopicSettings();

    function onTopicChange(topicId, event) {
        let newTopicIds;
        let newTachoTopicSettings = tachoTopicSettings;
        let newTriggers = reviewType.triggers ?? [];

        if (event.currentTarget.checked) {
            newTopicIds = [...reviewType.topicIds, topicId];
        } else {
            newTopicIds = reviewType.topicIds.filter((id) => id !== topicId);

            // Removing the Tacho Infringements topic drops its settings, so ticking it again
            // starts from an empty date with mark all as reviewed off.
            if (topicId === getTachoInfringementsTopicId(reviewOptions)) {
                newTachoTopicSettings = buildDefaultTachoTopicSettings();
            }

            // A trigger belongs to its topic, so removing the topic removes its triggers.
            newTriggers = newTriggers.filter((trigger) => trigger.topicId !== topicId);
        }

        onUpdate({
            ...reviewType,
            topicIds: newTopicIds,
            tachoTopicSettings: newTachoTopicSettings,
            triggers: newTriggers
        });
    }

    function onIncludeItemsFromChange(item) {
        onUpdate({
            ...reviewType,
            tachoTopicSettings: {
                ...tachoTopicSettings,
                includeItemsFrom: toCalendarDate(item)
            }
        });
    }

    // An invalid or out-of-range entry clears the date, so the required-date rule reports it
    // against the field and saving is blocked until a valid date is entered.
    function onIncludeItemsFromError() {
        onUpdate({
            ...reviewType,
            tachoTopicSettings: {
                ...tachoTopicSettings,
                includeItemsFrom: null
            }
        });
    }

    function onTriggersChange(triggers) {
        onUpdate({
            ...reviewType,
            triggers: triggers
        });
    }

    function onAllowMarkAllReviewedChange(event) {
        onUpdate({
            ...reviewType,
            tachoTopicSettings: {
                ...tachoTopicSettings,
                allowMarkAllReviewed: event.target.checked
            }
        });
    }

    // The distinct messages of the selected topics that need a permission, so an administrator
    // knows this review type can only be completed by users holding them. Topics that need no
    // permission, and topics left unselected, contribute nothing.
    function getSelectedPermissionMessages() {
        const messages = [];

        (reviewOptions?.topics ?? []).forEach((topic) => {
            const selected = reviewType?.topicIds?.some((id) => id === topic.id) === true;

            if (selected && topic.permissionMessage != null && topic.permissionMessage !== "" && !messages.includes(topic.permissionMessage)) {
                messages.push(topic.permissionMessage);
            }
        });

        return messages;
    }

    // A single requirement reads as a sentence on its own; more than one needs an opening line,
    // otherwise the messages run together as an unexplained list.
    function buildPermissionWarning(messages) {
        if (messages.length === 1) {
            return messages[0];
        }

        return (
            <Fragment>
                <div>{multiplePermissionsSummary}</div>
                <ul>
                    {messages.map((message) => {
                        return <li key={message}>{message}</li>;
                    })}
                </ul>
            </Fragment>
        );
    }

    const selectedPermissionMessages = getSelectedPermissionMessages();

    return (
        <div>
            <h2>Review Topics</h2>
            <FormHelperText loading={loading}>Choose the topics that make up this review.</FormHelperText>
            <div className="spacer-bottom">
                <GridLayout numberOfColumns={3} gap={20}>
                    {(reviewOptions?.topics ?? []).map((topic) => {
                        return (
                            <FormCheckbox
                                key={`review-topic-${topic.id}`}
                                id={`review-topic-${topic.id}`}
                                label={topic.name}
                                checked={reviewType?.topicIds?.some((id) => id === topic.id)}
                                disabled={saving || topic.enabled === false}
                                loading={loading}
                                onChange={(event) => onTopicChange(topic.id, event)}
                            />
                        );
                    })}
                </GridLayout>
                <ShowHide
                    evaluator={errors.topicIds != null}
                    show={(
                        <div className="form-field">
                            <FormHelperText loading={loading} danger={true}>{errors.topicIds}</FormHelperText>
                        </div>
                    )}
                />
            </div>
            <NotificationInline
                intent="warning"
                allowClose={false}
                show={selectedPermissionMessages.length > 0}
                text={buildPermissionWarning(selectedPermissionMessages)}
            />
            <ShowHide
                evaluator={isTachoInfringementsTopicSelected(reviewType, reviewOptions)}
                show={(
                    <div className="spacer-bottom review-type-topic-settings">
                        <h3 className={classNames({ "bp6-skeleton": loading })}>Tacho Infringements Settings</h3>
                        <FormDateInput
                            id="review-type-tacho-include-items-from"
                            headingText="Include infringements from"
                            helperText="Only infringements on or after this date are listed on reviews."
                            loading={loading}
                            disabled={saving}
                            value={tachoTopicSettings.includeItemsFrom}
                            minDate={includeItemsFromMinDate}
                            onChange={onIncludeItemsFromChange}
                            onError={onIncludeItemsFromError}
                            dangerHelperText={errors["tachoTopicSettings.includeItemsFrom"]}
                        />
                        <FormSwitch
                            id="review-type-tacho-allow-mark-all-reviewed"
                            headingText="Allow mark all as reviewed"
                            helperText="Lets the manager mark every outstanding infringement as reviewed in one step."
                            checked={tachoTopicSettings.allowMarkAllReviewed === true}
                            onChange={onAllowMarkAllReviewedChange}
                            loading={loading}
                            disabled={saving}
                        />
                    </div>
                )}
            />
            <ShowHide
                evaluator={getOfferedTriggerTypes(reviewType, reviewOptions).length > 0}
                show={(
                    <ReviewTriggersTable
                        heading="Triggers"
                        helperText="Choose what raises this review. Only triggers for the topics you have selected are shown."
                        triggers={reviewType?.triggers}
                        onChange={onTriggersChange}
                        topicIds={reviewType?.topicIds}
                        errors={triggerErrors}
                        reviewOptions={reviewOptions}
                        loading={loading}
                        saving={saving}
                        createMode={createMode}
                    />
                )}
            />
        </div>
    );
}

ReviewTypeTopicsStep.propTypes = {
    reviewType: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    errors: PropTypes.object,
    triggerErrors: PropTypes.object,
    reviewOptions: PropTypes.object,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    createMode: PropTypes.bool
};
